import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusBadge from './StatusBadge';

const FloorPlanView = ({ assets, onAssetClick, onLocationUpdate }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showLegend, setShowLegend] = useState(true);

  const rooms = [
    { id: 'room-101', name: 'Conference Room A', x: 50, y: 50, width: 120, height: 80, type: 'conference' },
    { id: 'room-102', name: 'IT Server Room', x: 200, y: 50, width: 100, height: 80, type: 'server' },
    { id: 'room-103', name: 'Open Office Area', x: 50, y: 160, width: 200, height: 120, type: 'office' },
    { id: 'room-104', name: 'Executive Office', x: 280, y: 160, width: 80, height: 60, type: 'office' },
    { id: 'room-105', name: 'Break Room', x: 280, y: 240, width: 80, height: 60, type: 'break' },
    { id: 'room-106', name: 'Storage Room', x: 320, y: 50, width: 60, height: 80, type: 'storage' },
    { id: 'room-107', name: 'Reception Area', x: 50, y: 300, width: 150, height: 80, type: 'reception' },
    { id: 'room-108', name: 'Training Room', x: 220, y: 320, width: 140, height: 80, type: 'training' }
  ];

  // Distribute assets across rooms
  const getAssetsInRoom = (roomId) => {
    const roomAssets = assets?.filter(asset => {
      // Simple distribution logic based on asset type and room type
      const roomIndex = rooms?.findIndex(r => r?.id === roomId);
      return (asset?.id % rooms?.length) === roomIndex;
    });
    return roomAssets?.slice(0, Math.min(roomAssets?.length, 8)); // Max 8 assets per room
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return '#059669'; // success
      case 'spare': return '#D97706'; // warning
      case 'repair': return '#DC2626'; // error
      case 'retired': return '#64748B'; // secondary
      default: return '#64748B';
    }
  };

  const getRoomColor = (type) => {
    switch (type) {
      case 'conference': return '#E0F2FE';
      case 'server': return '#FEF3C7';
      case 'office': return '#F0F9FF';
      case 'break': return '#F0FDF4';
      case 'storage': return '#FDF4FF';
      case 'reception': return '#FFF7ED';
      case 'training': return '#F8FAFC';
      default: return '#F8FAFC';
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 2));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleRoomClick = (room) => {
    setSelectedRoom(selectedRoom?.id === room?.id ? null : room);
  };

  const handleAssetLocationUpdate = (asset, newRoomId) => {
    onLocationUpdate(asset?.id, newRoomId);
    console.log(`Asset ${asset?.assetId} moved to ${newRoomId}`);
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle h-full flex flex-col">
      {/* Controls Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-card-foreground">Floor Plan View</h3>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomOut}
              disabled={zoomLevel <= 0.5}
            >
              <Icon name="ZoomOut" size={16} />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[60px] text-center">
              {Math.round(zoomLevel * 100)}%
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomIn}
              disabled={zoomLevel >= 2}
            >
              <Icon name="ZoomIn" size={16} />
            </Button>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLegend(!showLegend)}
            iconName={showLegend ? "EyeOff" : "Eye"}
            iconPosition="left"
          >
            {showLegend ? 'Hide' : 'Show'} Legend
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="RotateCcw"
            iconPosition="left"
            onClick={() => setZoomLevel(1)}
          >
            Reset View
          </Button>
        </div>
      </div>
      {/* Floor Plan Container */}
      <div className="flex-1 relative overflow-hidden">
        <div className="absolute inset-0 overflow-auto">
          <div 
            className="relative bg-muted/20 min-w-full min-h-full"
            style={{ 
              transform: `scale(${zoomLevel})`,
              transformOrigin: 'top left',
              width: `${100 / zoomLevel}%`,
              height: `${100 / zoomLevel}%`
            }}
          >
            {/* SVG Floor Plan */}
            <svg
              viewBox="0 0 400 400"
              className="w-full h-full"
              style={{ minHeight: '600px' }}
            >
              {/* Room Definitions */}
              {rooms?.map((room) => {
                const roomAssets = getAssetsInRoom(room?.id);
                const isSelected = selectedRoom?.id === room?.id;
                
                return (
                  <g key={room?.id}>
                    {/* Room Rectangle */}
                    <rect
                      x={room?.x}
                      y={room?.y}
                      width={room?.width}
                      height={room?.height}
                      fill={getRoomColor(room?.type)}
                      stroke={isSelected ? '#2563EB' : '#E2E8F0'}
                      strokeWidth={isSelected ? 2 : 1}
                      className="cursor-pointer hover:stroke-primary transition-all"
                      onClick={() => handleRoomClick(room)}
                    />
                    {/* Room Label */}
                    <text
                      x={room?.x + room?.width / 2}
                      y={room?.y + 15}
                      textAnchor="middle"
                      className="text-xs font-medium fill-current text-card-foreground"
                      style={{ fontSize: '10px' }}
                    >
                      {room?.name}
                    </text>
                    {/* Asset Count Badge */}
                    <circle
                      cx={room?.x + room?.width - 10}
                      cy={room?.y + 10}
                      r="8"
                      fill="#2563EB"
                      className="cursor-pointer"
                    />
                    <text
                      x={room?.x + room?.width - 10}
                      y={room?.y + 14}
                      textAnchor="middle"
                      className="text-xs font-bold fill-white"
                      style={{ fontSize: '8px' }}
                    >
                      {roomAssets?.length}
                    </text>
                    {/* Asset Indicators */}
                    {roomAssets?.slice(0, 6)?.map((asset, index) => {
                      const cols = Math.ceil(Math.sqrt(roomAssets?.length));
                      const row = Math.floor(index / cols);
                      const col = index % cols;
                      const spacing = 12;
                      const startX = room?.x + 10;
                      const startY = room?.y + 25;
                      
                      return (
                        <circle
                          key={asset?.id}
                          cx={startX + col * spacing}
                          cy={startY + row * spacing}
                          r="4"
                          fill={getStatusColor(asset?.status)}
                          className="cursor-pointer hover:r-5 transition-all"
                          onClick={(e) => {
                            e?.stopPropagation();
                            onAssetClick(asset);
                          }}
                          title={`${asset?.assetId} - ${asset?.type} (${asset?.status})`}
                        />
                      );
                    })}
                    {/* More Assets Indicator */}
                    {roomAssets?.length > 6 && (
                      <text
                        x={room?.x + 10}
                        y={room?.y + room?.height - 5}
                        className="text-xs fill-current text-muted-foreground"
                        style={{ fontSize: '8px' }}
                      >
                        +{roomAssets?.length - 6} more
                      </text>
                    )}
                  </g>
                );
              })}
              
              {/* Building Outline */}
              <rect
                x="40"
                y="40"
                width="350"
                height="350"
                fill="none"
                stroke="#64748B"
                strokeWidth="2"
                strokeDasharray="5,5"
              />
            </svg>
          </div>
        </div>

        {/* Legend */}
        {showLegend && (
          <div className="absolute top-4 right-4 bg-card border border-border rounded-lg p-4 shadow-elevated max-w-xs">
            <h4 className="text-sm font-semibold text-card-foreground mb-3">Legend</h4>
            
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Asset Status
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-success"></div>
                  <span>Active</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-warning"></div>
                  <span>Spare</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-error"></div>
                  <span>Repair</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-secondary"></div>
                  <span>Retired</span>
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-3 border-t border-border">
              <div className="text-xs text-muted-foreground">
                Click rooms to view details • Click dots to view assets
              </div>
            </div>
          </div>
        )}

        {/* Room Detail Panel */}
        {selectedRoom && (
          <div className="absolute bottom-4 left-4 bg-card border border-border rounded-lg p-4 shadow-elevated max-w-sm">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-card-foreground">
                {selectedRoom?.name}
              </h4>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedRoom(null)}
                className="h-6 w-6"
              >
                <Icon name="X" size={14} />
              </Button>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Total Assets:</span>
                <span className="font-medium">{getAssetsInRoom(selectedRoom?.id)?.length}</span>
              </div>
              
              <div className="space-y-1">
                {getAssetsInRoom(selectedRoom?.id)?.slice(0, 3)?.map((asset) => (
                  <div key={asset?.id} className="flex items-center justify-between text-xs">
                    <span className="font-mono">{asset?.assetId}</span>
                    <StatusBadge status={asset?.status} size="sm" />
                  </div>
                ))}
                {getAssetsInRoom(selectedRoom?.id)?.length > 3 && (
                  <div className="text-xs text-muted-foreground">
                    +{getAssetsInRoom(selectedRoom?.id)?.length - 3} more assets
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloorPlanView;